import React, { Fragment, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { getLessonCompletionDisplayPercent } from 'utils/reportsHelpers'
import AdminGradeList from 'components/AdminGradeList'
import AdminBHULessonProgressCard from 'components/AdminBHULessonProgressCard'
import AdminNoClasses from 'components/AdminNoClasses'
import PlaybookSteps from 'components/admin/PlaybookSteps'
import { LESSON_COMPLETION_SPECIALIZED_UNITS_LABEL } from 'components/AdminSiteHeader/constants'
import { FLAG_CORE_LESSON_COMPLETION_UPDATES } from 'components/AdminDashboardBanner/constants'
import { isFlagOn } from 'utils/featureFlags'
import { CLASSES_NOT_STARTED_MESSAGE, NOT_STARTED_MESSAGE } from './constants'
import {
  BodyWrapper,
  ClassesProgressWrapper,
  ClassProgressBullet,
  ClassesNotStartedText,
  ClassesNotStartedWrapper,
  ClassesStartedText,
  CardHeading,
  GoalTextClasses,
  LessonCompletionWrapper,
  NoClassesMessage,
  PanelWrapper,
  PipeSeparator,
  SiteCollapsible,
  StyledAdminLessonProgressTabs,
  StyledHairline,
  StyledHeader,
  StyledHeaderProgressBar,
} from './component.styles'

const LessonCompletionAccordion = ({
  activeIndex,
  classesCreated,
  classesNotStarted,
  grades,
  handleAccordionClick,
  index,
  lessonCompletionUnitsLabel,
  name,
  siteId,
  sitePreferences,
  summary,
}) => {
  const lessonCompletionBarMounted = useRef(true)
  useEffect(
    () => () => {
      lessonCompletionBarMounted.current = false
    },
    [],
  )

  const lessonCompletionPercentage = getLessonCompletionDisplayPercent(summary)
  const isSpecializedUnits =
    Boolean(lessonCompletionUnitsLabel) &&
    lessonCompletionUnitsLabel === LESSON_COMPLETION_SPECIALIZED_UNITS_LABEL
  const hasBHUEnabled =
    sitePreferences?.mspDigital?.bullyingHarassmentUnitEnabled ?? false
  const hasBHUClassCreated = summary?.unit5?.classesCreated > 0
  const bhuStatus = !isSpecializedUnits && hasBHUEnabled && hasBHUClassCreated
  const showEmptyDwbReport =
    isSpecializedUnits && isFlagOn([FLAG_CORE_LESSON_COMPLETION_UPDATES])

  return (
    <SiteCollapsible
      accordionPanelDataTestId={`school-row`}
      ariaLabel={`${name}-accordion`}
      classesNotStarted={classesNotStarted}
      dataTestId={`site-collapsible-${siteId}`}
      followActiveProp={true}
      isActive={activeIndex === index}
      key={siteId}
      onClick={() => handleAccordionClick(index)}
      title={
        <PanelWrapper>
          <StyledHeader classesNotStarted={classesNotStarted}>
            {name}
          </StyledHeader>
          <ClassesProgressWrapper classesNotStarted={classesNotStarted}>
            {classesCreated === 0 && (
              <NoClassesMessage>{CLASSES_NOT_STARTED_MESSAGE}</NoClassesMessage>
            )}
            {classesCreated > 0 && (
              <Fragment key="classes-created-key">
                <Fragment key="classes-not-started-key">
                  <Fragment key="classes-created-goal-key">
                    <ClassesStartedText>{classesCreated}</ClassesStartedText>
                    {!isSpecializedUnits && (
                      <Fragment key="classes-creation-goal">
                        <PipeSeparator>|</PipeSeparator>
                        <GoalTextClasses>
                          {`Goal: 
                      ${
                        sitePreferences?.k5Digital.classCreationGoalValue
                          ? sitePreferences?.k5Digital.classCreationGoalValue
                          : 'Not Set'
                      }`}
                        </GoalTextClasses>
                      </Fragment>
                    )}
                  </Fragment>
                  {classesNotStarted > 0 && (
                    <ClassesNotStartedWrapper>
                      <ClassProgressBullet />
                      <ClassesNotStartedText>
                        {classesNotStarted} {NOT_STARTED_MESSAGE}
                      </ClassesNotStartedText>
                    </ClassesNotStartedWrapper>
                  )}
                </Fragment>
              </Fragment>
            )}
          </ClassesProgressWrapper>
          <LessonCompletionWrapper
            classesCreated={classesCreated}
            isDwb={isSpecializedUnits}
          >
            <StyledHeaderProgressBar
              $fillPercent={lessonCompletionPercentage}
              $isDwb={isSpecializedUnits}
              isParentMounted={lessonCompletionBarMounted}
              keyText={`lesson-completion-header-${siteId}`}
              percentComplete={lessonCompletionPercentage}
              shouldRenderIndicator={classesCreated > 0}
              shouldRenderPercentage
            />
            {!isSpecializedUnits && (
              <Fragment key="lesson-completion-goal">
                <PipeSeparator>|</PipeSeparator>
                <GoalTextClasses>{`Goal: 
              ${
                sitePreferences?.k5Digital.lessonCompletionGoalValue
                  ? `${sitePreferences?.k5Digital.lessonCompletionGoalValue}%`
                  : 'Not Set'
              }`}</GoalTextClasses>
              </Fragment>
            )}
          </LessonCompletionWrapper>
        </PanelWrapper>
      }
    >
      {!isSpecializedUnits && (
        <PlaybookSteps
          key={`monitor-${siteId}`}
          showMonitorViewOnly={true}
          siteId={siteId}
          sitePreferences={sitePreferences}
        />
      )}
      <BodyWrapper
        classesCreated={classesCreated}
        isDwbEmptyReport={showEmptyDwbReport}
      >
        {showEmptyDwbReport ? (
          <AdminNoClasses isDwbEmptyReport />
        ) : isSpecializedUnits ? (
          <Fragment key="specialized-body-wrapper">
            {classesCreated === 0 && <AdminNoClasses />}
            {classesCreated > 0 && (
              <AdminGradeList
                dwbProgressBars
                grades={grades}
                siteId={siteId}
                splitTwoColumns
              />
            )}
          </Fragment>
        ) : (
          <Fragment key="body-wrapper">
            {classesCreated === 0 && <AdminNoClasses />}
            {classesCreated > 0 && (
              <Fragment key="classes-created-body">
                <CardHeading>Core Curriculum (Units 1-4)</CardHeading>
                <StyledAdminLessonProgressTabs bhuStatus={bhuStatus} />
                <StyledHairline />
                <AdminGradeList grades={grades} siteId={siteId} />
              </Fragment>
            )}
          </Fragment>
        )}
      </BodyWrapper>
      {bhuStatus && (
        <AdminBHULessonProgressCard
          classesCreated={classesCreated}
          grades={grades}
          siteId={siteId}
          summary={summary}
        />
      )}
    </SiteCollapsible>
  )
}

LessonCompletionAccordion.propTypes = {
  activeIndex: PropTypes.number,
  classesCreated: PropTypes.number,
  classesNotStarted: PropTypes.number,
  grades: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  handleAccordionClick: PropTypes.func,
  index: PropTypes.number,
  lessonCompletionUnitsLabel: PropTypes.string,
  name: PropTypes.string,
  siteId: PropTypes.number,
  sitePreferences: PropTypes.object,
  summary: PropTypes.object,
}

export default LessonCompletionAccordion
